import React, { useEffect, useState, useRef } from 'react';
import MapView from './MapView';
import type { MapConfig } from '../api/JsdhNotifikaceService/generated';

interface MapZoomWrapperProps {
    gps: string;
    hasCisloPopisne: boolean;
    showHyndrant: boolean;
    height?: number | string;
    mapConfig?: MapConfig;
    routeCoordinates?: [number, number][];
    stationGps?: string;
    fitToBounds?: boolean;
}

const MapZoomWrapper: React.FC<MapZoomWrapperProps> = ({
                                                           gps,
                                                           hasCisloPopisne,
                                                           showHyndrant,
                                                           height = 550,
                                                           mapConfig,
                                                           routeCoordinates,
                                                           stationGps,
                                                           fitToBounds = false,
                                                       }) => {
    const [zoomLevel, setZoomLevel] = useState(() => (hasCisloPopisne ? 15 : 14));
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Záměrně závisíme na primitivních polích `mapConfig`, ne na celé referenci.
    // V mock módu se `mapConfig` objekt překlápí každou sekundu (nový tick), zatímco hodnoty zůstávají stejné.
    // Bez tohoto by se cycle interval reset každou sekundu a `setTimeout` nikdy nedoběhl pro intervaly > 1 s.
    const mode = mapConfig?.mode || 'auto';
    const fixedZoom = mapConfig?.fixedZoom;
    const zoom1 = mapConfig?.zoom1;
    const zoom2 = mapConfig?.zoom2;
    const interval = mapConfig?.interval;

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Skip zoom cycling when fitToBounds is active (route view handles its own zoom)
        if (fitToBounds) return;

        // Fixed mode - no cycling
        if (mode === 'fixed') {
            setZoomLevel(fixedZoom ?? 15);
            return;
        }

        // Cycle mode - custom zoom levels
        if (mode === 'cycle') {
            const z1 = zoom1 ?? 15;
            const z2 = zoom2 ?? 17;
            const intervalMs = interval ?? 3500;
            setZoomLevel(z1);

            const scheduleNext = (current: number) => {
                timeoutRef.current = setTimeout(() => {
                    const nextZoom = current === z1 ? z2 : z1;
                    setZoomLevel(nextZoom);
                    scheduleNext(nextZoom);
                }, intervalMs);
            };
            scheduleNext(z1);

            return () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
            };
        }

        // Auto mode - original behavior
        const initialZoom = hasCisloPopisne ? 15 : 14;
        setZoomLevel(initialZoom);

        const getNextZoom = (current: number): number => {
            if (!hasCisloPopisne) {
                return current === 14 ? 15 : 14;
            }
            return current === 15 ? 17 : 15;
        };

        const getDelay = (current: number): number => {
            if (!hasCisloPopisne) return 3500;
            if (showHyndrant && current === 17) return 7000;
            return 3500;
        };

        const scheduleNext = (current: number) => {
            const delay = getDelay(current);

            timeoutRef.current = setTimeout(() => {
                const nextZoom = getNextZoom(current);
                setZoomLevel(nextZoom);
                scheduleNext(nextZoom);
            }, delay);
        };

        scheduleNext(initialZoom);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [hasCisloPopisne, showHyndrant, mode, fixedZoom, zoom1, zoom2, interval, fitToBounds]);

    return (
        <MapView
            gps={gps}
            hasCisloPopisne={hasCisloPopisne}
            height={height}
            showHyndrant={showHyndrant}
            zoomLevel={zoomLevel}
            routeCoordinates={routeCoordinates}
            stationGps={stationGps}
            fitToBounds={fitToBounds}
        />
    );
};

export default React.memo(MapZoomWrapper);
